import React, { useRef, useState, useEffect, useMemo } from "react";
import { useParams, useHistory, Switch } from "react-router-dom";
import { DOC_MEMBERSHIP_TRANSFER_OUT_LINK } from "@icoach/documents/membership/staticData";
import MembershipTransferOutContent from "@icoach/documents/membership/transferOut/MembershipTransferOutContent";
import MembershipTransferOutHistory from "@icoach/documents/membership/transferOut/MembershipTransferOutHistory";
import MembershipTransferOutSearchBarTabs from "@icoach/documents/membership/transferOut/MembershipTransferOutSearchBarTabs";
import ConfirmTemporaryContractDialog from "@icoach/documents/dialog/ConfirmTemporaryContractDialog";
import { BlankFormBox, SearchBar } from "@icoach/documents/components/";
import { CLUB_DOCUMENTS_ROUTER } from "@icoach/router/routerPath";
import useDocMembershipTransferOutApi from "@apis/useDocMembershipTransferOutApi";
import { PEOPLE_TYPE } from "@icoach/constants/";
import PrivateRoute from "@common/components/route/PrivateRoute";
import { Typography, Box } from "@curves/ui-base";
import { TRANSFEROUT_MOVETYPE } from "@icoach/constants/";
import { isEqual } from "@util/utils";

const MembershipTransferOutPage = props => {
	const {
		getInitTransferOutUnMoveoutOptionApi,
		getInitTransferOutMoveoutOptionApi,
		getDocTemporaryTransferOutContractApi,
		deleteDocTemporaryTransferOutContractApi,
	} = useDocMembershipTransferOutApi();
	const { peopleType: peopleTypeProps, moveoutType: moveoutTypeProps, referenceID: referenceIDProps } = useParams();
	const history = useHistory();
	const [options, setOptions] = useState({});
	const [openDialog, setOpenDialog] = useState(false); //有暫存資料時的彈出視窗
	const [isMounted, setMounted] = useState(false);
	const searchRef = useRef([]);
	const temporaryIDRef = useRef(0);

	const checkPeopleType = opts => {
		const { targetOptions = [] } = opts;
		const isInclude = targetOptions.find(({ value }) => isEqual(peopleTypeProps, value));
		const type = isEqual(moveoutTypeProps, TRANSFEROUT_MOVETYPE.MOVEOUT) ? PEOPLE_TYPE.UNCONFIRMTRANSFEROUTMEMBER : PEOPLE_TYPE.MEMBER;
		if (!peopleTypeProps || !isInclude) {
			const path = CLUB_DOCUMENTS_ROUTER.TRANSFEROUT.GET_PEOPLETYPE_PATH(moveoutTypeProps, type);
			history.replace(path);
		}
	};

	// 重設 按鈕
	const handleResetClick = type => {
		const path = CLUB_DOCUMENTS_ROUTER.TRANSFEROUT.GET_PEOPLETYPE_PATH(moveoutTypeProps, type);
		history.replace(path);
	};

	// 搜尋 按鈕
	const handleSearchHistoryClick = id => {
		const { targetID, targetType } = searchRef.current.getResult();
		const path = CLUB_DOCUMENTS_ROUTER.TRANSFEROUT.GET_HISTORY_PATH(moveoutTypeProps, targetType, targetID);
		history.replace(path);
	};

	// 新增 按鈕
	const handleCreateClick = () => {
		const { targetID: id, targetType: type } = searchRef.current.getResult();
		const params = { id, type };
		doCreateBlankContractApi(params, type, id);
	};

	// 彈出視窗 要刪除暫存資料
	const handleDialogNoClick = () => {
		const params = {
			id: referenceIDProps,
			type: peopleTypeProps,
		};
		doDeleteTemporaryDataApi(params);
	};

	// 彈出視窗 讀取上次暫存資料繼續編輯
	const handleDialogYseClick = () => {
		if (!temporaryIDRef.current) return;
		const contractID = temporaryIDRef.current;
		const path = CLUB_DOCUMENTS_ROUTER.TRANSFEROUT.GET_CONTRACT_PATH(TRANSFEROUT_MOVETYPE.UNMOVEOUT, peopleTypeProps, referenceIDProps, contractID);
		setOpenDialog(false);
		history.push(path);
	};

	// 刪除暫存合約資料
	const doDeleteTemporaryDataApi = async parmas => {
		const resp = await deleteDocTemporaryTransferOutContractApi(parmas);
		if (resp) {
			const contractID = 0;
			const path = CLUB_DOCUMENTS_ROUTER.TRANSFEROUT.GET_CONTRACT_PATH(TRANSFEROUT_MOVETYPE.UNMOVEOUT, peopleTypeProps, referenceIDProps, contractID);
			setOpenDialog(false);
			history.push(path);
		}
	};

	// 新增空白頁面
	const doCreateBlankContractApi = async (params, peopleType, referenceID) => {
		let temporaryID;
		temporaryIDRef.current = 0;
		temporaryID = await getDocTemporaryTransferOutContractApi(params);
		if (temporaryID !== 0 && !!temporaryID) {
			temporaryIDRef.current = temporaryID;
			setOpenDialog(true);
		} else {
			const contractID = 0;
			const path = CLUB_DOCUMENTS_ROUTER.TRANSFEROUT.GET_CONTRACT_PATH(TRANSFEROUT_MOVETYPE.UNMOVEOUT, peopleType, referenceID, contractID);
			history.push(path);
		}
	};

	// 初始search options
	const doInitOptionsApi = async () => {
		let resp;
		if (isEqual(moveoutTypeProps, TRANSFEROUT_MOVETYPE.UNMOVEOUT)) {
			resp = await getInitTransferOutUnMoveoutOptionApi();
		} else if (isEqual(moveoutTypeProps, TRANSFEROUT_MOVETYPE.MOVEOUT)) {
			resp = await getInitTransferOutMoveoutOptionApi();
		} else {
			history.push(CLUB_DOCUMENTS_ROUTER.TRANSFEROUT.GET_MOVEOUTTYPE_PATH(TRANSFEROUT_MOVETYPE.UNMOVEOUT));
		}
		if (resp) {
			checkPeopleType(resp);
			setOptions(resp);
			setMounted(true);
		}
	};

	const useSearchBarMemo = useMemo(
		() => {
			return (
				<SearchBar
					ref={searchRef}
					className="mb-4"
					TabsComponentProps={<MembershipTransferOutSearchBarTabs />}
					sourceOption={options.targetOptions}
					onResetEvent={handleResetClick}
					onCreateEvent={handleCreateClick}
					onSearchHistoryEvent={handleSearchHistoryClick}
					routerPath={CLUB_DOCUMENTS_ROUTER.TRANSFEROUT.GET_MOVEOUTTYPE_PATH(moveoutTypeProps)}
				/>
			);
		},
		// eslint-disable-next-line
		[options, moveoutTypeProps]
	);

	useEffect(
		() => {
			doInitOptionsApi();
			return () => setMounted(false);
		},
		// eslint-disable-next-line
		[moveoutTypeProps]
	);

	return (
		<Box className="container main-container-spacing">
			{isMounted && (
				<React.Fragment>
					<Box component="header" className="page-header">
						<Typography className="page-header-title" variant="h3">
							國內移籍申請書 (移出)
						</Typography>
						<Box className="btn-group">
							<BlankFormBox sourceData={DOC_MEMBERSHIP_TRANSFER_OUT_LINK} />
						</Box>
					</Box>
					{useSearchBarMemo}
					<Switch>
						<PrivateRoute exact path={CLUB_DOCUMENTS_ROUTER.TRANSFEROUT.HISTORY} as={MembershipTransferOutHistory} onBlankContractClick={handleCreateClick} />
						<PrivateRoute
							exact
							path={CLUB_DOCUMENTS_ROUTER.TRANSFEROUT.CONTRACT_UNMOVEOUT}
							as={MembershipTransferOutContent}
							searchRef={searchRef}
							onReturnHistory={handleSearchHistoryClick}
							isReadOnly={false}
						/>
						<PrivateRoute
							exact
							path={CLUB_DOCUMENTS_ROUTER.TRANSFEROUT.READONLY_UNMOVEOUT}
							as={MembershipTransferOutContent}
							searchRef={searchRef}
							onReturnHistory={handleSearchHistoryClick}
							isReadOnly={true}
						/>
						<PrivateRoute
							exact
							path={CLUB_DOCUMENTS_ROUTER.TRANSFEROUT.READONLY_MOVEOUT}
							as={MembershipTransferOutContent}
							searchRef={searchRef}
							onReturnHistory={handleSearchHistoryClick}
							isReadOnly={true}
						/>
					</Switch>
					<ConfirmTemporaryContractDialog open={openDialog} handleClose={() => setOpenDialog(false)} handleClickYes={handleDialogYseClick} handleClickNo={handleDialogNoClick} />
				</React.Fragment>
			)}
		</Box>
	);
};

export default MembershipTransferOutPage;
